import React, { useState, useEffect, Fragment } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Label, Row, Col, Button,
} from 'reactstrap';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Formik, Field, Form, ErrorMessage,
} from 'formik';
import { post, get } from '../../helpers/api_helper';

function buildManifestValues(manifest) {
  return manifest.map((row, index) => ({
    manifest_key: `${row.sale_item_id}-${row.item.id}-${index}`,
    sale_item_id: row.sale_item_id,
    qty: row.required,
    item_id: row.item.id,
  }));
}

function groupRowsByManifest(values) {
  return values.items.reduce((acc, row, index) => {
    if (!acc[row.manifest_key]) {
      acc[row.manifest_key] = [];
    }

    acc[row.manifest_key].push({ row, index });

    return acc;
  }, {});
}

function OrderExportForm(props) {
  const {
    entity,
    t,
    sale,
  } = props;

  const [, setLoading] = useState(false);
  const [, setErrorMessage] = useState(null);
  const [manifest, setManifest] = useState([]);
  const [mv, setMv] = useState(null);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  useEffect(() => {
    const getManifest = async () => {
      try {
        const mani = await get(`/api/sales/distribution/manifest/${sale.id}`);

        setManifest(mani);
        setMv(buildManifestValues(mani));
      } catch (error) {
        toastr.error(error.message, 'Error');
      }
    };

    if (sale.id !== undefined) {
      getManifest();
    }
  }, [sale.id]);

  const save = async (values) => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const result = await post('/api/sales/distribution/create', {
        sale_id: sale.id,
        destination: values.destination,
        items: values.items.map((item) => ({
          sale_item_id: item.sale_item_id,
          item_id: Number(item.item_id),
          qty: Number(item.qty) || 0,
        })),
      });

      toastr.success(t(`Export ID ${result.id} saved`), t('Success'));
      props.onSuccess({
        id: result.id,
      });
    } catch (error) {
      setErrorMessage(`${error.message}: ${error.response?.data || ''}`);
    } finally {
      setLoading(false);
    }
  };

  const validate = (values) => {
    const errors = {};
    const itemErrors = [];
    const groupedRows = groupRowsByManifest(values);

    if (!values.destination || values.destination.trim() === '') {
      errors.destination = props.t(t('Destination is required'));
    }

    values.items.forEach((item, index) => {
      const rowErrors = {};
      const qty = Number(item.qty);

      if (Number.isNaN(qty)) {
        rowErrors.qty = props.t(t('Quantity is required'));
      } else if (qty < 0) {
        rowErrors.qty = props.t(t('Quantity cannot be negative'));
      }

      if (!item.item_id) {
        rowErrors.item_id = props.t(t('Item ID is required'));
      }

      if (Object.keys(rowErrors).length > 0) {
        itemErrors[index] = rowErrors;
      }
    });

    manifest.forEach((row, manifestIndex) => {
      const manifestKey = `${row.sale_item_id}-${row.item.id}-${manifestIndex}`;
      const grouped = groupedRows[manifestKey] || [];
      const totalQty = grouped.reduce((acc, entry) => acc + (Number(entry.row.qty) || 0), 0);

      if (totalQty > Number(row.required)) {
        grouped.forEach(({ index }) => {
          itemErrors[index] = {
            ...(itemErrors[index] || {}),
            qty: props.t(t(`Split total cannot exceed required qty of ${row.required}`)),
          };
        });
      }
    });

    if (itemErrors.length > 0) {
      errors.items = itemErrors;
    }

    return errors;
  };

  const jsx = (
    <Row>
      <Col xxl="12">
        {mv !== null && (
          <Formik
            enableReinitialize
            initialValues={
              {
                sale_id: sale.id,
                destination: entity.destination || '',
                items: mv,
              }
            }
            validate={validate}
            onSubmit={(values) => {
              save(values);
            }}
          >
            {({
              errors, values, setFieldValue,
            }) => {
              const manifestGroups = groupRowsByManifest(values);

              return (
                <Form className="mt-3" id="OrderExportForm" autoComplete="off">
                  <div className="mb-3">
                    <Label
                      for="destination"
                    >
                      {props.t('Destination')}
                    </Label>
                    <Field placeholder="shopjimmy.dropzone.parts" className="form-control" id="destination" name="destination" />
                    <ErrorMessage
                      name="destination"
                      component="div"
                      className="mt-2 text-danger"
                    />
                  </div>

                  <h6 className="mt3">{t('Items Manifest')}</h6>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>{' '}</th>
                        <th>{t('ID')}</th>
                        <th>{t('Part Number')}</th>
                        <th>{t('Required')}</th>
                        <th>{t('Split Qty')}</th>
                        <th>{t('Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifest.length !== undefined && manifest.map((row, ri) => {
                        const manifestKey = `${row.sale_item_id}-${row.item.id}-${ri}`;
                        const groupedRows = manifestGroups[manifestKey] || [];
                        const totalQty = groupedRows.reduce(
                          (acc, entry) => acc + (Number(entry.row.qty) || 0),
                          0,
                        );
                        const remainingQty = Number(row.required) - totalQty;
                        const options = [row.item, ...row.subs];

                        return (
                          <Fragment key={manifestKey}>
                            <tr>
                              <td colSpan="6">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span>{row.listing.title}</span>
                                  <span className={remainingQty === 0 ? 'text-success' : 'text-danger'}>
                                    {t('Assigned')}
                                    {': '}
                                    {totalQty}
                                    {' / '}
                                    {row.required}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {groupedRows.map(({ row: splitRow, index: itemIndex }, splitIndex) => (
                              <tr key={`${manifestKey}-${itemIndex}`}>
                                <td>
                                  {splitIndex === 0 && (
                                    <img className="rounded" style={{ maxWidth: '3rem', maxHeight: '3rem' }} src={`/api/items/thumbnail/${row.item.id}`} alt={row.item.part_number} />
                                  )}
                                </td>
                                <td>
                                  <a href={`/item/${splitRow.item_id}`}>
                                    {splitRow.item_id}
                                  </a>
                                </td>
                                <td>
                                  <span className="text-dark">{`Original: ${row.item.part_number}`}</span>
                                  <Field
                                    name={`items[${itemIndex}].item_id`}
                                    id={`itemId${itemIndex}`}
                                    as="select"
                                    className="form-control mt-1"
                                  >
                                    {options.map((option) => (
                                      <option key={option.id} value={option.id}>
                                        {`${option.manufacturer} ${option.part_number} - ${
                                          option.stock.reduce((a, c) => a + c.qty, 0)
                                        }`}
                                      </option>
                                    ))}
                                  </Field>
                                  <ErrorMessage
                                    name={`items[${itemIndex}].item_id`}
                                    component="div"
                                    className="mt-2 text-danger"
                                  />
                                </td>
                                <td>{row.required}</td>
                                <td>
                                  <Field
                                    className="form-control"
                                    name={`items[${itemIndex}].qty`}
                                    type="number"
                                    min="0"
                                    step="1"
                                  />
                                  <ErrorMessage
                                    name={`items[${itemIndex}].qty`}
                                    component="div"
                                    className="mt-2 text-danger"
                                  />
                                </td>
                                <td className="text-nowrap">
                                  <Button
                                    type="button"
                                    color="light"
                                    className="me-2"
                                    onClick={() => {
                                      const currentItems = [...values.items];

                                      currentItems.splice(itemIndex + 1, 0, {
                                        manifest_key: manifestKey,
                                        sale_item_id: row.sale_item_id,
                                        qty: 0,
                                        item_id: row.item.id,
                                      });

                                      setFieldValue('items', currentItems);
                                    }}
                                  >
                                    {t('Split')}
                                  </Button>
                                  {groupedRows.length > 1 && (
                                    <Button
                                      type="button"
                                      color="danger"
                                      onClick={() => {
                                        const currentItems = values.items.filter(
                                          (_, idx) => idx !== itemIndex,
                                        );

                                        setFieldValue('items', currentItems);
                                      }}
                                    >
                                      {t('Remove')}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                            <tr>
                              <td colSpan="6">
                                <h6>{t('Stock')}</h6>
                                {[row.item, ...row.subs].map((stockItem) => (
                                  <Fragment key={stockItem.id}>
                                    <div className="mb-2">
                                      <strong>
                                        {stockItem.id === row.item.id ? `${t('Original')}: ` : `${t('Substitute')}: `}
                                        {`${stockItem.manufacturer} ${stockItem.part_number}`}
                                      </strong>
                                      {stockItem.stock.map((stock) => (
                                        <div key={`${stockItem.id}-${stock.source}`}>
                                          <b>
                                            <Button
                                              type="button"
                                              color="link"
                                              className="p-0 m-0"
                                              onClick={() => { setFieldValue('destination', stock.source); }}
                                            >
                                              {stock.source}
                                            </Button>
                                          </b>
                                          {' '}
                                          {stock.qty}
                                          {' '}
                                          {t('available')}
                                          {' at '}
                                          {stock.cost}
                                          {' '}
                                          {t('each')}
                                        </div>
                                      ))}
                                    </div>
                                  </Fragment>
                                ))}
                              </td>
                            </tr>
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>

                  {typeof errors.items === 'string' && (
                    <div className="text-danger">{errors.items}</div>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </Col>
    </Row>
  );

  return jsx;
}

OrderExportForm.defaultProps = {
  entity: {
    destination: '',
  },
  sale: {},
  onSuccess: () => { },
};

OrderExportForm.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  entity: PropTypes.object,
  sale: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default connect(() => ({

}), {})(withTranslation()(OrderExportForm));
